import { useEffect, useState } from "react";
import { api, fmtMoney, today } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Truck, Eye, CheckCircle2, XCircle, AlertTriangle, Copy, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TRANS_MODES = [
    { v: "1", label: "1 — Road" },
    { v: "2", label: "2 — Rail" },
    { v: "3", label: "3 — Air" },
    { v: "4", label: "4 — Ship" },
];

export default function EWayBills() {
    const [invoices, setInvoices] = useState([]);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({
        vehicle_no: "", vehicle_type: "R", transporter_name: "", transporter_id: "",
        trans_mode: "1", trans_distance: 0, trans_doc_no: "", trans_doc_date: today(),
    });
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showRawJson, setShowRawJson] = useState(false);

    const load = async () => {
        const { data } = await api.get("/invoices", { params: { type: "invoice" } });
        setInvoices(data);
    };
    useEffect(() => { load(); }, []);

    const openSheet = (id) => {
        setSelected(id); setOpen(true); setPreview(null);
        setShowRawJson(false);
        setForm({ vehicle_no: "", vehicle_type: "R", transporter_name: "", transporter_id: "", trans_mode: "1", trans_distance: 0, trans_doc_no: "", trans_doc_date: today() });
    };

    const runPreview = async () => {
        if (!selected) return;
        setLoading(true);
        try {
            const { data } = await api.post(`/e-way-bills/${selected}/preview`, form);
            setPreview(data);
        } catch (e) {
            toast.error("Preview failed");
        } finally { setLoading(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post(`/e-way-bills/${selected}`, form);
            toast.success("E-Way Bill generated");
            setOpen(false);
            await load();
        } catch (err) {
            const msg = err.response?.data?.detail?.message || err.response?.data?.detail || "Generation failed";
            toast.error(typeof msg === "string" ? msg : "Validation failed — see preview");
        } finally { setSubmitting(false); }
    };

    return (
        <>
            <PageHeader kicker="TAXES" title="E-Way Bills" subtitle="Goods movement compliance (NIC schema v1.03) — transporter, vehicle, route, and validation." />
            <PageBody>
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    <table className="fin-table">
                        <thead><tr><th>Invoice</th><th>Customer</th><th className="num">Amount</th><th>Status</th><th></th></tr></thead>
                        <tbody>
                            {invoices.slice(0, 50).map((i) => (
                                <tr key={i.id}>
                                    <td className="font-mono text-[12.5px]">{i.number}</td>
                                    <td>{i.customer_name}</td>
                                    <td className="num">{fmtMoney(i.total, i.currency)}</td>
                                    <td>{i.e_way_bill?.eway_no ? <span className="text-positive font-mono text-[11.5px]">{i.e_way_bill.eway_no}</span> : <span className="text-muted-foreground text-[12px]">Not generated</span>}</td>
                                    <td>
                                        {!i.e_way_bill?.eway_no && (
                                            <Button size="sm" variant="outline" onClick={() => openSheet(i.id)} data-testid={`eway-gen-${i.id}`}>
                                                <Truck className="w-3.5 h-3.5 mr-1" />Generate
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </PageBody>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full sm:max-w-[680px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4">
                        <div className="text-[10px] font-semibold tracking-[0.18em] text-[#a68a56]">E-WAY BILL — NIC SCHEMA v1.03</div>
                        <SheetTitle className="text-xl font-semibold tracking-tight">Generate E-Way Bill</SheetTitle>
                    </SheetHeader>
                    <form className="flex-1 overflow-y-auto p-6 space-y-4" onSubmit={submit}>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Vehicle number</Label>
                                <Input className="mt-1.5" value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value.toUpperCase() })} placeholder="MH12AB1234" data-testid="eway-vehicle-no" />
                            </div>
                            <div>
                                <Label>Vehicle type</Label>
                                <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="R">R — Regular</SelectItem>
                                        <SelectItem value="O">O — Over-dimensional cargo</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Transporter name</Label>
                                <Input className="mt-1.5" value={form.transporter_name} onChange={(e) => setForm({ ...form, transporter_name: e.target.value })} />
                            </div>
                            <div>
                                <Label>Transporter ID (TRANSIN)</Label>
                                <Input className="mt-1.5" value={form.transporter_id} onChange={(e) => setForm({ ...form, transporter_id: e.target.value })} placeholder="15-char GSTIN" />
                            </div>
                            <div>
                                <Label>Mode</Label>
                                <Select value={form.trans_mode} onValueChange={(v) => setForm({ ...form, trans_mode: v })}>
                                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {TRANS_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Distance (km)</Label>
                                <Input type="number" className="mt-1.5" value={form.trans_distance} onChange={(e) => setForm({ ...form, trans_distance: Number(e.target.value) })} data-testid="eway-distance" />
                            </div>
                            <div>
                                <Label>LR / Transport Doc No</Label>
                                <Input className="mt-1.5" value={form.trans_doc_no} onChange={(e) => setForm({ ...form, trans_doc_no: e.target.value })} />
                            </div>
                            <div>
                                <Label>Transport Doc Date</Label>
                                <Input type="date" className="mt-1.5" value={form.trans_doc_date} onChange={(e) => setForm({ ...form, trans_doc_date: e.target.value })} />
                            </div>
                        </div>

                        {/* Preview button */}
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={runPreview} disabled={loading} data-testid="eway-preview-btn">
                                {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
                                Preview & validate
                            </Button>
                            <Button type="submit" disabled={submitting || (preview && !preview.valid)} data-testid="eway-submit-btn">
                                <Truck className="w-4 h-4 mr-1.5" /> Generate E-Way Bill
                            </Button>
                        </div>

                        {preview && (
                            <div className="space-y-3 border-t border-border pt-4">
                                <div className={`rounded-md border p-3 flex items-start gap-3 ${preview.valid ? "border-[#1f6c47]/30 bg-[#1f6c47]/8" : "border-[#b4233a]/40 bg-[#b4233a]/8"}`}>
                                    {preview.valid ? <CheckCircle2 className="w-5 h-5 text-[#1f6c47] mt-0.5" /> : <XCircle className="w-5 h-5 text-[#b4233a] mt-0.5" />}
                                    <div className="text-sm">
                                        <div className="font-semibold">{preview.valid ? "Valid — ready to submit to NIC" : `${preview.error_count} error${preview.error_count !== 1 ? "s" : ""} block submission`}</div>
                                        {preview.warning_count > 0 && <div className="text-[12px] text-muted-foreground mt-0.5">{preview.warning_count} warning{preview.warning_count !== 1 ? "s" : ""}</div>}
                                    </div>
                                </div>
                                {preview.issues?.length > 0 && (
                                    <div className="border border-border rounded-md divide-y divide-border max-h-[160px] overflow-y-auto">
                                        {preview.issues.map((iss, idx) => (
                                            <div key={idx} className="px-3 py-2 flex items-start gap-2.5 text-[12.5px]">
                                                {iss.severity === "error" ? <XCircle className="w-4 h-4 text-[#b4233a] mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-[#c97a3a] mt-0.5 shrink-0" />}
                                                <div><span className="font-mono text-[10.5px] text-muted-foreground mr-2">{iss.code}</span><span>{iss.message}</span></div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <FormattedEwayBill payload={preview.payload} form={form} />
                                <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <button
                                            type="button"
                                            onClick={() => setShowRawJson((v) => !v)}
                                            className="flex items-center gap-1 text-[10.5px] tracking-[0.16em] font-semibold text-muted-foreground hover:text-foreground transition-colors"
                                            data-testid="eway-toggle-raw"
                                        >
                                            {showRawJson ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                            RAW PAYLOAD (NIC SCHEMA v1.03)
                                        </button>
                                        {showRawJson && (
                                            <Button type="button" size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(JSON.stringify(preview.payload, null, 2)); toast.success("JSON copied"); }}>
                                                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                                            </Button>
                                        )}
                                    </div>
                                    {showRawJson && (
                                        <pre className="bg-[#0f1424] text-[#e5e7eb] text-[11px] leading-[1.5] rounded-md p-3 max-h-[280px] overflow-auto font-mono" data-testid="eway-raw-json">{JSON.stringify(preview.payload, null, 2)}</pre>
                                    )}
                                </div>
                            </div>
                        )}
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
}

/* ---------- Formatted E-Way Bill preview (human-readable) ---------- */
const MODE_LABELS = { "1": "Road", "2": "Rail", "3": "Air", "4": "Ship" };

function FormattedEwayBill({ payload, form }) {
    return (
        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="eway-formatted">
            <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between flex-wrap gap-2">
                <div>
                    <div className="text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">E-Way Bill · NIC preview</div>
                    <div className="text-[16px] font-semibold mt-0.5">Doc #{payload.docNo} <span className="text-muted-foreground font-mono text-[13px]">· {payload.docDate}</span></div>
                </div>
                <div className="text-right text-[11.5px]">
                    <div className="uppercase tracking-[0.14em] text-muted-foreground">Mode · Distance</div>
                    <div className="font-mono tabular-nums">{MODE_LABELS[String(payload.transMode)] || payload.transMode} · {payload.transDistance} km</div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-b border-border">
                <div className="p-4">
                    <div className="text-[9.5px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Consignor (from)</div>
                    <div className="mt-1 text-[14px] font-semibold">{payload.fromTrdName || "—"}</div>
                    <div className="text-[11.5px] font-mono mt-1">GSTIN {payload.fromGstin}</div>
                    <div className="text-[12px] text-muted-foreground mt-1">
                        {[payload.fromAddr1, payload.fromAddr2].filter(Boolean).join(", ")}
                        <div>{[payload.fromPlace, payload.fromPincode].filter(Boolean).join(" · ")}</div>
                        <div>State code: <span className="font-mono">{payload.fromStateCode}</span></div>
                    </div>
                </div>
                <div className="p-4 border-l border-border">
                    <div className="text-[9.5px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Consignee (to)</div>
                    <div className="mt-1 text-[14px] font-semibold">{payload.toTrdName || "—"}</div>
                    <div className="text-[11.5px] font-mono mt-1">GSTIN {payload.toGstin}</div>
                    <div className="text-[12px] text-muted-foreground mt-1">
                        {[payload.toAddr1, payload.toAddr2].filter(Boolean).join(", ")}
                        <div>{[payload.toPlace, payload.toPincode].filter(Boolean).join(" · ")}</div>
                        <div>State code: <span className="font-mono">{payload.toStateCode}</span></div>
                    </div>
                </div>
            </div>

            <div className="p-4 border-b border-border">
                <div className="text-[9.5px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2">Transport</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12.5px]">
                    <KV label="Vehicle #" value={payload.vehicleNo || "—"} mono />
                    <KV label="Vehicle type" value={payload.vehicleType === "R" ? "Regular" : "ODC"} />
                    <KV label="Transporter" value={payload.transporterName || "—"} />
                    <KV label="Transporter ID" value={payload.transporterId || "—"} mono />
                    <KV label="LR / doc no" value={payload.transDocNo || "—"} mono />
                    <KV label="Doc date" value={payload.transDocDate || "—"} mono />
                </div>
            </div>

            <div className="p-4">
                <div className="text-[9.5px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2">Line items</div>
                <div className="border border-border rounded-md overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead className="bg-muted/40">
                            <tr>
                                <th className="text-left px-2 py-1.5 font-semibold">Product</th>
                                <th className="text-left px-2 py-1.5 font-semibold">HSN</th>
                                <th className="text-right px-2 py-1.5 font-semibold">Qty</th>
                                <th className="text-right px-2 py-1.5 font-semibold">Taxable</th>
                                <th className="text-right px-2 py-1.5 font-semibold">Tax %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(payload.itemList || []).map((it, i) => (
                                <tr key={i} className="border-t border-border">
                                    <td className="px-2 py-1.5">{it.productName}</td>
                                    <td className="px-2 py-1.5 font-mono text-[11px]">{it.hsnCode}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{it.quantity} <span className="text-muted-foreground">{it.qtyUnit}</span></td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtMoney(it.taxableAmount)}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{(it.cgstRate + it.sgstRate + it.igstRate).toFixed(1)}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-[12.5px]">
                    <div />
                    <div className="space-y-1 min-w-0">
                        <div className="flex justify-between gap-4"><span className="text-muted-foreground">Taxable value</span><span className="tabular-nums">{fmtMoney(payload.totalValue)}</span></div>
                        {payload.cgstValue > 0 && <div className="flex justify-between gap-4"><span className="text-muted-foreground">CGST</span><span className="tabular-nums">{fmtMoney(payload.cgstValue)}</span></div>}
                        {payload.sgstValue > 0 && <div className="flex justify-between gap-4"><span className="text-muted-foreground">SGST</span><span className="tabular-nums">{fmtMoney(payload.sgstValue)}</span></div>}
                        {payload.igstValue > 0 && <div className="flex justify-between gap-4"><span className="text-muted-foreground">IGST</span><span className="tabular-nums">{fmtMoney(payload.igstValue)}</span></div>}
                        {payload.cessValue > 0 && <div className="flex justify-between gap-4"><span className="text-muted-foreground">Cess</span><span className="tabular-nums">{fmtMoney(payload.cessValue)}</span></div>}
                        <div className="flex justify-between gap-4 pt-2 mt-2 border-t border-border font-semibold text-[13.5px]">
                            <span>Total invoice value</span>
                            <span className="tabular-nums" data-testid="eway-total-value">{fmtMoney(payload.totInvValue)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function KV({ label, value, mono }) {
    return (
        <div className="min-w-0">
            <div className="text-[9.5px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
            <div className={`mt-0.5 truncate ${mono ? "font-mono text-[12px]" : ""}`} title={String(value)}>{value}</div>
        </div>
    );
}
